/**
 * Anonymous, fire-and-forget analytics beacon for the chatbot widget.
 *
 * Sends a single POST to {base}/event. Never throws, never retries, never blocks
 * the UI. Skipped entirely in demo mode and when no siteKey is present. De-dupes
 * per page load via a window-scoped flag so the embedded and floating IIFE bundles
 * (which both merge window.ChatbotWidget) cannot double-fire a once-per-page event.
 *
 * @param {object} config - validated widget config (siteKey, sessionId, clientId, apiEndpoint, mode, isFloating)
 * @param {'impression'|'panel_opened'} type
 * @param {{ once?: boolean }} [opts]
 */
export function trackEvent(config, type, opts = {}) {
  try {
    // Never track in demo mode, and skip when there's no siteKey to attribute to.
    if (!config || config.mode === 'demo' || !config.siteKey) return;

    // Page-level dedupe across BOTH bundles. Module scope is per-bundle, so the
    // guard must live on window. Keyed by event type so impression and
    // panel_opened de-dupe independently.
    const store = (window.__lbAnalytics = window.__lbAnalytics || {});
    if (opts.once) {
      if (store[type]) return;
      store[type] = true;
    }

    const url = getEventUrl(config.apiEndpoint);
    if (!url) return;

    const payload = {
      type,
      surface: config.isFloating ? 'floating' : 'embedded',
      sessionId: config.sessionId,
      clientId: config.clientId,
      path: location.pathname,          // pathname only — no query string (privacy)
      referrer: document.referrer || null,
      ts: Date.now()
    };

    // keepalive lets the request survive a navigation (page-view proxy on unload-ish
    // flows). Not navigator.sendBeacon: it can't set the x-site-key request header.
    // .catch swallows everything — analytics must never surface an error to the host page.
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-site-key': config.siteKey
      },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit'
    }).catch(() => {});
  } catch (_) {
    // Absolutely never throw from the analytics path.
  }
}

/**
 * Derive {base}/event from the configured /chat endpoint. Mirrors the anchored
 * regex in ChatAPI.getRequestUrl() so the two stay consistent (do not use the
 * weaker unanchored replace from history-api.js).
 */
function getEventUrl(apiEndpoint) {
  if (!apiEndpoint || typeof apiEndpoint !== 'string') return null;
  return apiEndpoint.replace(/\/chat\/?$/, '') + '/event';
}
